import getValue from 'get-value';
import type {
  ScreenSchema,
  ScreenSlot,
  IteratedScreenSlot,
  AnySlotPart
} from './screen-schemas/types';
import { isIteratedSlot, isInlineIteratedPart } from './screen-schemas/types';
export interface NarratableScreen {
  id?: string;
  audio?: string;
  speech?: string;
  'text-audio'?: string;
  /**
   * When set (e.g. hotspot synthetic lightbox shells), JiT/S3 cache keys use
   * `id` but translation lookups use this value — matches `uc-text` `:k` keys.
   */
  'translation-id'?: string;
  'dont-read'?: boolean;
  template?: string;
  'placeholder-values'?: Record<string, string | number>;
  [key: string]: unknown;
}

/** `screen-hotspot.vue` lightbox id (`lightbox_${hostId}_${targetName}`). */
export const hotspotTargetLightboxId = (hostId: string, targetName: string) =>
  `lightbox_${hostId}_${targetName}`;

/** Translation key prefix for hotspot target rows (YAML `id` when present). */
export const hotspotTargetTranslationId = (
  targetRow: Record<string, unknown>,
  lightboxId: string
): string => {
  const rowId = targetRow.id as string | undefined;
  return rowId?.trim() ? rowId : lightboxId;
};

/**
 * Synthetic lightbox shell from a hotspot target row (matches `lightboxScreen()`
 * in `screen-hotspot.vue`). Cache key stays `lightbox-${lightboxId}`; narration
 * text resolves via `translation-id` when the row has its own `id`.
 */
export function synthHotspotTargetLightboxShell(
  hostId: string,
  targetName: string,
  targetRow: Record<string, unknown>
): NarratableScreen {
  const lightboxId = hotspotTargetLightboxId(hostId, targetName);
  const translationId = hotspotTargetTranslationId(targetRow, lightboxId);
  return {
    id: lightboxId,
    ...(translationId !== lightboxId
      ? { 'translation-id': translationId }
      : {}),
    template: 'lightbox',
    heading: targetRow.heading,
    subheading: targetRow.subheading,
    title: targetRow.title,
    text: targetRow.text,
    instructions: targetRow.instructions,
    question: targetRow.question,
    ...(Array.isArray(targetRow.templates)
      ? { templates: targetRow.templates as NarratableScreen[] }
      : {})
  };
}

export type ResolveTextFn = (
  key: string,
  value: string | number,
  values?: Record<string, string | number>
) => string | null;

/** Returns true for values that are safe to pass as narration text. */
export const isTextValue = (v: unknown): v is string | number =>
  typeof v === 'string' || typeof v === 'number';

export function readPlaceholderValues(
  host: Record<string, unknown> | null | undefined
): Record<string, string | number> {
  if (!host) return {};
  const v = host['placeholder-values'];
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: Record<string, string | number> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'string' || typeof val === 'number') out[k] = val;
  }
  return out;
}

export function extractPartsText(
  parts: AnySlotPart[],
  obj: Record<string, unknown>,
  resolveText: (key: string, value: string | number) => string | null
): (string | null)[] {
  return parts.map(part => {
    if (isInlineIteratedPart(part)) {
      const items = getValue(obj, part.iterateField) as unknown[] | undefined;
      if (!Array.isArray(items)) return null;
      const hostId = obj.id as string | undefined;
      const itemTexts = items
        .map((rawItem, index) => {
          const item = rawItem as Record<string, unknown>;
          const rowId = item.id as string | undefined;
          const itemForKeys =
            rowId || !(hostId && part.itemKeyPath)
              ? item
              : { ...item, id: `${hostId}.${part.itemKeyPath}.${index}` };
          return extractPartsText(part.parts, itemForKeys, resolveText)
            .map(p => p?.trim())
            .filter(Boolean)
            .join('\n\n');
        })
        .filter(Boolean);
      return itemTexts.join('\n\n') || null;
    }

    if (part.omitIfPresent?.some(f => getValue(obj, f))) return null;
    const id = obj.id as string | undefined;
    const value = getValue(obj, part.field);
    if (isTextValue(value)) {
      const key = id ? `${id}.${part.field}` : part.field;
      return resolveText(key, value);
    }
    for (const fb of part.fallback ?? []) {
      const fbValue = getValue(obj, fb);
      if (isTextValue(fbValue)) {
        const key = id ? `${id}.${fb}` : fb;
        return resolveText(key, fbValue);
      }
    }
    return null;
  });
}

export function buildSlotText(
  slot: ScreenSlot,
  obj: Record<string, unknown>,
  resolveText: (key: string, value: string | number) => string | null
): string {
  return extractPartsText(slot.parts, obj, resolveText)
    .map(p => p?.trim())
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

export interface ScreenAudioTextResolver {
  resolveAudioKey(slotName: string): string;
  getSlotText(slotName: string): string;
  getIntroJitText(): string;
}

export const narrationTextId = (
  screen: NarratableScreen
): string | undefined => {
  const tid = screen['translation-id'] as string | undefined;
  if (tid?.trim()) return tid;
  return screen.id;
};

export function createScreenAudioTextResolver(options: {
  screen: NarratableScreen;
  schema: ScreenSchema;
  resolveText: ResolveTextFn;
}): ScreenAudioTextResolver {
  const { screen: scr, schema: sch, resolveText } = options;
  const fill = readPlaceholderValues(scr as unknown as Record<string, unknown>);
  const resolveWithFill = (key: string, value: string | number) =>
    resolveText(key, value, fill) ?? '';

  const textScreen = (): Record<string, unknown> => {
    const textId = narrationTextId(scr);
    return textId && textId !== scr.id
      ? { ...(scr as Record<string, unknown>), id: textId }
      : (scr as Record<string, unknown>);
  };

  const markerKeyForSlot = (
    markerKey: string | undefined,
    index?: number
  ): string | undefined => {
    if (!markerKey || !scr?.id) return undefined;
    return markerKey
      .replace(/\$\{id\}/g, scr.id)
      .replace(/\$\{index\}/g, index !== undefined ? String(index) : '');
  };

  const resolveAudioKey = (slotName: string): string => {
    if (slotName === 'intro') {
      if (!scr?.id) return '';
      if (sch?.introKeyMode === 'lightbox') return `lightbox-${scr.id}`;
      if (sch?.introKeySuffix) return `${scr.id}${sch.introKeySuffix}`;
      return scr.id;
    }

    const namedSlot = sch.slots[slotName];
    if (namedSlot && !isIteratedSlot(namedSlot) && namedSlot.markerKey) {
      const key = markerKeyForSlot(namedSlot.markerKey);
      if (key) return key;
    }

    for (const slot of Object.values(sch.slots)) {
      if (!isIteratedSlot(slot)) continue;

      const prefix = slot.keyPrefix + '-';
      if (!slotName.startsWith(prefix)) continue;

      const index = parseInt(slotName.slice(prefix.length), 10);
      if (isNaN(index)) continue;

      const markerKey = markerKeyForSlot(slot.markerKey, index);
      if (markerKey) return markerKey;

      if (slot.useItemId) {
        const items = getValue(scr, slot.iterate) as unknown[] | undefined;
        const item = items?.[index] as Record<string, unknown> | undefined;
        const itemId = item?.id as string | undefined;
        if (itemId) return `${itemId}-${slot.keyPrefix}-${index}`;
      }
      break;
    }

    return `${scr?.id}-${slotName.replace(/\./g, '-')}`;
  };

  const getScreenSlotText = (slot: ScreenSlot): string =>
    buildSlotText(slot, textScreen(), resolveWithFill);

  const getItemSlotText = (
    item: Record<string, unknown>,
    index: number,
    slot: IteratedScreenSlot
  ): string => {
    // Synthesize the item id when absent so translation keys stay
    // `${scr.id}-${keyPrefix}-${index}.${field}` (matching the audio markers),
    // then reuse the shared extractor so nested `InlineIteratedPart`s (e.g. a
    // `templates[]` chain inside the item) are folded in recursively.
    const itemWithId = item.id
      ? item
      : { ...item, id: `${scr?.id}-${slot.keyPrefix}-${index}` };

    return buildSlotText({ parts: slot.parts }, itemWithId, resolveWithFill);
  };

  const getSlotText = (slotName: string): string => {
    if (!sch || !scr) return '';

    const slotDef = sch.slots[slotName];

    if (slotDef) {
      if (isIteratedSlot(slotDef)) return '';
      return getScreenSlotText(slotDef);
    }

    for (const slot of Object.values(sch.slots)) {
      if (!isIteratedSlot(slot)) continue;

      const prefix = slot.keyPrefix + '-';
      if (!slotName.startsWith(prefix)) continue;

      const index = parseInt(slotName.slice(prefix.length), 10);
      if (isNaN(index)) continue;

      const items = getValue(scr, slot.iterate) as unknown[] | undefined;
      if (!Array.isArray(items)) continue;

      const item = items[index] as Record<string, unknown> | undefined;
      if (!item) continue;

      return getItemSlotText(item, index, slot);
    }

    return '';
  };

  const getIntroJitText = (): string => {
    if (scr['text-audio']) {
      const textId = narrationTextId(scr) ?? scr.id;
      return resolveWithFill(
        `${textId}.text-audio`,
        scr['text-audio'] as string | number
      );
    }
    return getSlotText('intro');
  };

  return { resolveAudioKey, getSlotText, getIntroJitText };
}
